<script setup lang="ts">
import { parseChangelogNotes, type NoteBlock } from '~/utils/clientChangelog'

const props = defineProps<{ text: string, lang: string }>()

type Section =
  | { kind: 'list', items: Extract<NoteBlock, { kind: 'item' }>[] }
  | Extract<NoteBlock, { kind: 'paragraph' | 'heading' }>

// Consecutive items render as one list.
const sections = computed(() => parseChangelogNotes(props.text).reduce<Section[]>((all, block) => {
  const last = all[all.length - 1]
  if (block.kind !== 'item') all.push(block)
  else if (last?.kind === 'list') last.items.push(block)
  else all.push({ kind: 'list', items: [block] })
  return all
}, []))
</script>

<template lang="pug">
.cl-notes(:lang="lang")
  template(v-for="(section, index) in sections" :key="index")
    ul.cl-notes-list(v-if="section.kind === 'list'")
      li.cl-notes-item(
        v-for="(item, itemIndex) in section.items"
        :key="itemIndex"
        :class="{ 'is-sub': item.depth === 1, 'is-numbered': item.marker }"
        :data-marker="item.marker ? `${item.marker}.` : undefined"
      )
        template(v-for="(part, partIndex) in item.inlines" :key="partIndex")
          strong(v-if="part.kind === 'strong'") {{ part.text }}
          code(v-else-if="part.kind === 'code'") {{ part.text }}
          a(v-else-if="part.kind === 'link'" :href="part.href" target="_blank" rel="noopener noreferrer") {{ part.text }}
          template(v-else) {{ part.text }}
    component.cl-notes-text(v-else :is="section.kind === 'heading' ? 'h4' : 'p'" :class="{ 'is-heading': section.kind === 'heading' }")
      template(v-for="(part, partIndex) in section.inlines" :key="partIndex")
        strong(v-if="part.kind === 'strong'") {{ part.text }}
        code(v-else-if="part.kind === 'code'") {{ part.text }}
        a(v-else-if="part.kind === 'link'" :href="part.href" target="_blank" rel="noopener noreferrer") {{ part.text }}
        template(v-else) {{ part.text }}
</template>

<style lang="postcss" scoped>
/* The global p/span/heading rule sets a color; notes inherit instead. */
.cl-notes :where(h4, p, span) {
  color: inherit;
}

.cl-notes {
  display: grid;
  gap: 0.7rem;
  color: var(--cl-copy);
  font-size: 0.95rem;
  line-height: 1.7;
  overflow-wrap: anywhere;
}

.cl-notes-list {
  display: grid;
  gap: 0.35rem;
}

.cl-notes-item {
  position: relative;
  padding-left: 1.15rem;
}

.cl-notes-item::before {
  content: '';
  position: absolute;
  top: 0.78em;
  left: 0.1rem;
  width: 0.42rem;
  height: 1px;
  background: var(--cl-line-strong);
}

.cl-notes-item.is-numbered {
  padding-left: 1.6rem;
}

.cl-notes-item.is-numbered::before {
  content: attr(data-marker);
  top: 0;
  width: auto;
  height: auto;
  background: none;
  color: var(--cl-faint);
  font-variant-numeric: tabular-nums;
}

.cl-notes-item.is-sub {
  margin-left: 1.15rem;
  font-size: 0.9rem;
}

.cl-notes-item.is-sub::before {
  top: 0.72em;
  width: 0.22rem;
  height: 0.22rem;
  border-radius: 999px;
}

.cl-notes-text.is-heading {
  color: var(--cl-text);
  font-size: 0.95rem;
  font-weight: 700;
}

.cl-notes strong {
  color: var(--cl-text);
  font-weight: 600;
}

.cl-notes code {
  padding: 0.05rem 0.35rem;
  border-radius: 0.3rem;
  background: var(--cl-surface-2);
  color: var(--cl-text);
  font-size: 0.85em;
}

.cl-notes a {
  color: var(--cl-text);
  text-decoration: underline;
  text-decoration-color: var(--cl-line-strong);
  text-underline-offset: 0.2em;
  transition: text-decoration-color 0.2s ease;
}

.cl-notes a:hover {
  text-decoration-color: currentColor;
}
</style>
